import type { ThemeProps } from '@/types/theme';

export type Category =
  | 'People & Culture'
  | 'Heritage & Traditions'
  | 'Wildlife & Coexistence'
  | 'Culinary Journeys'
  | 'Artisan & Craft'
  | 'Experiences';

export const KNOWN_THEME_IDS = [
  'page-flip',
  'scrolling-long-form',
  'grid',
  'photo-essay',
  'news-column',
  'magazine-grid',
  'minimal-one-pager',
  'timeline',
  'data-viz',
  'podcast-transcript',
] as const;

export type ThemeId = (typeof KNOWN_THEME_IDS)[number];

export interface Article {
  id: string;
  title: string;
  excerpt: string;
  category: Category;
  author: string;
  readTime: number;
  featured?: boolean;
  gradientFrom: string;
  gradientTo: string;
  /** Reporter theme used to render this story — each article showcases a different layout */
  theme: ThemeId;
  content: ThemeProps;
}

// ---------------------------------------------------------------------------
// Individual article content
// ---------------------------------------------------------------------------

const baobabContent: ThemeProps = {
  hero: {
    title: 'The Last Baobab Guardians of Limpopo',
    subtitle:
      'A San family has tended the same ancient grove for eleven generations. Now climate pressure and commercial interest are testing an eleven-century bond.',
    coverImage: 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?auto=format&fit=crop&w=1280&h=720&q=80',
    author: 'Nomvula Dlamini',
    authorAvatar: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=64&h=64&q=80',
    publishedAt: '2026-05-15T06:00:00Z',
    readingTimeMinutes: 9,
    breadcrumbs: [
      { label: 'Home', href: '/' },
      { label: 'Wildlife & Coexistence', href: '/' },
    ],
  },
  body: {
    html: `<p>On a bend where the Limpopo River curves northward and the sun bleaches the red clay to amber, there is a grove of baobabs that no map has ever formally named. The San families who have lived alongside it for more than a millennium call it <em>xaro</em> — a word that approximates, imperfectly, to "where the knowing stays."</p>

<p>The oldest tree is thought to be eleven hundred years old. Its trunk, more than eleven metres in circumference, bears the deep horizontal scars of elephant tusk and, lower down, the faint smudge of generations of human hands pressed into bark. The woman most responsible for that continuity today is Khoisan Nxau, 68, who has never lived anywhere else and who describes her relationship with the trees not as stewardship, but as kinship.</p>

<h2 id="eleven-generations">Eleven Generations of Knowing</h2>
<p>"My great-great-grandmother's grandmother planted her hands into this bark," Nxau says, touching the tree's wrinkled surface the way you might touch the shoulder of a sleeping elder. "When a baobab remembers you, it changes shape a little. It grows around the memory."</p>

<p>The claim is metaphorical — though not entirely unsupported by science. Baobabs form mycorrhizal root networks that allow trees to share nutrients across a grove. What San communities have known intuitively for centuries, dendrochronologists are now beginning to measure. The grove's 34 mature trees range from 200 to 1 100 years old, and each is connected to the others by a root web that Nxau describes, without any apparent irony, as a family WhatsApp group.</p>

<blockquote><p>"When the ancient trees in Zimbabwe died suddenly in 2018, we felt it here. The grove went quiet for three days. No wind. The elephants didn't come. We knew something had happened to relatives we had never met."</p></blockquote>

<h2 id="documenting-the-loss">Documenting Before the Loss</h2>
<p>In 2018, a study in <em>Nature Plants</em> documented the sudden death of nine of Africa's oldest baobabs — trees that had survived two millennia — apparently linked to climate stress and extended drought. For Nxau, the news triggered an urgency she had been suppressing for years.</p>

<p>She and her daughter Khoisan Sera began a systematic documentation project: measuring trunk circumference, recording the animals and insects that use each tree, photographing bark patterns, and — most critically — recording the oral knowledge that exists nowhere else. Four hundred pages of handwritten notes. Twelve hundred photographs. Six hours of recorded testimony across three languages.</p>

<p>"The knowledge is not only in our heads," Sera explains. "It's in the trees. When a tree dies, you lose the record of everything that happened near it — every elephant that sheltered beneath it, every birth, every ceremony, every drought that came and went."</p>

<h3 id="what-the-trees-know">What the Trees Know, That We Don't</h3>
<p>The documentation is unusual in its ambition. Beyond botanical data, the project records what the family calls "tree personality" — which baobabs attract bee colonies (a drought indicator, Sera explains, because bees leave when root water drops), which ones the elephants visit preferentially, which ones produce fruit that has a slightly different flavour profile that the family uses medicinally.</p>

<p>"Western science keeps rediscovering things we have always known," Nxau says. "The mycorrhizal network paper came out a few years ago. We read it and laughed — not because it was wrong, but because they were so surprised. We have been talking to trees through the ground our whole lives."</p>

<h2 id="the-pressure">Commercial Pressure, Formal Protection</h2>
<p>The land surrounding the grove is not formally protected. Three years ago it was earmarked for a safari lodge development. The project stalled — partly because of community objection, partly because of unresolved title deed complications — but it could restart. A neighbouring conservancy has offered to absorb the grove, which would provide formal protection but also shift decision-making authority away from the family. Nxau is deliberating.</p>

<p>"They are good people. But they count the trees in rands. We count them in years. Those are not the same currency."</p>

<p>By late afternoon, the baobabs undergo their daily transformation — swelling with the last light, turning from grey to amber to a warm orange that makes the grove feel briefly lit from inside. In that light, it is easy to understand why a family would bind itself to a place across eleven generations.</p>

<p>The question is whether that binding — woven from memory, observation, and love — can protect what formal law and commercial interest have not yet been asked, formally, to protect. Nxau doesn't frame it as a question. She frames it as a debt.</p>

<p>"The baobab has kept us alive for a thousand years," she says, walking back toward the compound as the first stars come up over the grove. "We owe it the same."</p>`,
    tableOfContents: [
      { id: 'eleven-generations', label: 'Eleven Generations of Knowing', level: 1 },
      { id: 'documenting-the-loss', label: 'Documenting Before the Loss', level: 1 },
      { id: 'what-the-trees-know', label: "What the Trees Know", level: 2 },
      { id: 'the-pressure', label: 'Commercial Pressure', level: 1 },
    ],
  },
  gallery: [
    {
      src: 'https://images.unsplash.com/photo-1547036967-23d11aacaee0?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Ancient baobab trunk with elephant scar marks',
      caption: 'The oldest baobab in the grove, approximately 1 100 years old',
      credit: 'Nomvula Dlamini',
    },
    {
      src: 'https://images.unsplash.com/photo-1528360983277-13d401cdc186?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Khoisan Nxau measuring trunk circumference',
      caption: 'Nxau documents each tree using hand tools passed down from her mother',
      credit: 'Nomvula Dlamini',
    },
    {
      src: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Baobab grove at golden hour',
      caption: 'The grove at last light — 34 mature trees across three hectares',
      credit: 'Nomvula Dlamini',
    },
    {
      src: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Handwritten field notes documenting the grove',
      caption: 'Four hundred pages of notes, written in Khoekhoegowab, Venda, and English',
      credit: 'Khoisan Sera',
    },
  ],
  footer: {
    authorName: 'Nomvula Dlamini',
    authorBio:
      'Nomvula Dlamini is an environmental journalist and ethnobotanist based in Polokwane. She has spent fifteen years documenting indigenous land knowledge across the Limpopo basin.',
    authorAvatar: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=64&h=64&q=80',
    tags: ['baobab', 'san', 'limpopo', 'conservation', 'indigenous-knowledge', 'climate'],
    relatedArticles: [
      { title: 'Who Owns the Wildlife? Community Conservancies Reshape the Map', href: '/story/community-conservancies' },
      { title: "Forty Years of Watching Elephants Remember", href: '/story/elephant-memory' },
      { title: "Venda Pottery's Unbroken Thread", href: '/story/venda-pottery' },
    ],
    shareUrl: 'https://reporter.marome.io/story/baobab-guardians',
  },
};

const hoedspruitKitchensContent: ThemeProps = {
  hero: {
    title: "Hoedspruit's Bush Kitchens Are Rewriting Safari Dining",
    subtitle:
      'A new generation of chefs is swapping hotel banquet menus for fire pits and foraged marula — and building hyperlocal tasting experiences that rival anything in Cape Town.',
    coverImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1280&h=720&q=80',
    author: 'Thabo Khumalo',
    authorAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=64&h=64&q=80',
    publishedAt: '2026-05-10T07:00:00Z',
    readingTimeMinutes: 6,
    breadcrumbs: [
      { label: 'Home', href: '/' },
      { label: 'Culinary Journeys', href: '/' },
    ],
  },
  body: {
    html: `<p>The fire has been going since 4 a.m. Chef Lindiwe Mthembu arrives at the open-air kitchen as the bush is still dark, checking the temperature of the coals by holding her palm above the grate the way her grandmother taught her: three seconds for braai heat, one second for baking heat, half a second for searing.</p>

<p>By 7 a.m. she has prepared marula-glazed kudu ribs for the first table of the day — guests from Amsterdam who booked six months ago and who will later tell her, with the particular sincerity of people who have never expected to feel this way about breakfast, that it is the best meal they have ever eaten outdoors.</p>

<h2 id="the-shift">The Shift in Safari Dining</h2>
<p>Hoedspruit has always had good lodges. What it didn't always have was chefs who refused to import their menus from Cape Town or Johannesburg. That started to change around 2022, when a cohort of young cooks — most of them trained at the country's formal culinary schools, but unwilling to replicate the fine-dining orthodoxy they'd been taught — began arriving in the Lowveld and asking a different question: what does <em>this place</em> taste like?</p>

<p>The answer, it turns out, is complex and constantly changing with the seasons. Marula fruit ripens between January and March, producing a golden, citrus-adjacent pulp that Mthembu uses in marinades, ferments into a light beer, and dehydrates into a powder she shakes over game carpaccio. Wild garlic clusters near the river between May and July. Sour figs grow on the kopjes. And mopane worms — a divisive ingredient among international guests, beloved by nearly everyone local — are available dried almost year-round.</p>

<blockquote><p>"The menu changes every week, sometimes every day. I cannot write it more than 48 hours in advance. If you want the same dinner twice, you have to come back in a different season."</p></blockquote>

<h2 id="beyond-game-meat">Beyond Game Meat</h2>
<p>The safari dining cliché — impala loin, crocodile skewer, ostrich burger — is still available in Hoedspruit if you want it. What the new bush kitchens are doing is layering that protein-forward tradition with a rigorous attention to wild plants, local grains, and the fermentation techniques that indigenous communities have used for centuries.</p>

<p>Chef Siphamandla Dlamin, whose fire kitchen sits on the edge of a 4 000-hectare private reserve, sources almost exclusively from within 80 kilometres. His vegetable programme includes heritage maize varieties grown by a cooperative of small-scale farmers an hour to the south, sorghum from a family operation in Tzaneen, and a rotating selection of dried legumes and seeds that arrive by informal barter with three neighbouring communities.</p>

<p>"I want every plate to contain the address of where we are," Dlamin says. "Not just Africa in some general sense. This specific valley. These specific rains."</p>

<h3 id="the-economics">The Economics Are Different Too</h3>
<p>The bush kitchen model is not cheap — nothing about bespoke fire cooking and hyperlocal sourcing is — but it is economically different from the imported-luxury model. More money stays local. The farmers who supply the kitchens have contracted income. The foragers — often community members from nearby villages with traditional ecological knowledge — are paid for their expertise, not their labour.</p>

<p>Mthembu estimates that 70% of what she spends on ingredients goes to people within 50 kilometres. "In the old model, everything came from a Johannesburg wholesaler. The lodge got the prestige; the community got the cleaning jobs. We're trying to flip that."</p>

<h2 id="the-future">Where It Goes from Here</h2>
<p>Two of the Hoedspruit chefs are in early conversations with a culinary school in Pretoria about a bush-kitchen curriculum — a programme that would bring students into the Lowveld for an immersive foraging and fire-cooking semester. If it gets off the ground, it could change the training pipeline that has historically sent young South African cooks toward European technique and away from indigenous knowledge.</p>

<p>As for Mthembu, she is already thinking about the dry season: the shift from marula to winter citrus, from fresh herbs to dried roots, from the abundance of summer to the austere complexity that the Lowveld produces when water is scarce. "That's when the real creativity happens," she says. "When you have less to work with, you learn what you actually know."</p>`,
    tableOfContents: [
      { id: 'the-shift', label: 'The Shift in Safari Dining', level: 1 },
      { id: 'beyond-game-meat', label: 'Beyond Game Meat', level: 1 },
      { id: 'the-economics', label: 'The Economics Are Different Too', level: 2 },
      { id: 'the-future', label: 'Where It Goes from Here', level: 1 },
    ],
  },
  gallery: [
    {
      src: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Open fire bush kitchen at dawn',
      caption: 'Chef Mthembu checks the coals at first light — the fire has been going since 4 a.m.',
      credit: 'Thabo Khumalo',
    },
    {
      src: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Marula-glazed kudu ribs plated on slate',
      caption: 'Marula glaze and wild herb crust on kudu ribs — the breakfast centrepiece',
      credit: 'Thabo Khumalo',
    },
    {
      src: 'https://images.unsplash.com/photo-1466637574441-749b8f19452f?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Wild herbs laid out on a wooden table',
      caption: 'Foraged greens sourced within 20 km of the kitchen',
      credit: 'Siphamandla Dlamin',
    },
    {
      src: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Bush dining table set against the sunset',
      caption: 'The outdoor dining terrace — roof is a canvas of stars',
      credit: 'Thabo Khumalo',
    },
    {
      src: 'https://images.unsplash.com/photo-1500937386664-56d1dfef3854?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Heritage maize drying on a smallholder farm',
      caption: 'Heritage maize from a cooperative 60 km south — one of seven local suppliers',
      credit: 'Thabo Khumalo',
    },
  ],
  footer: {
    authorName: 'Thabo Khumalo',
    authorBio:
      'Thabo Khumalo writes about food, farming, and the politics of the plate across southern Africa. He is the author of <em>Fire and Field</em> (2025).',
    authorAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=64&h=64&q=80',
    tags: ['food', 'safari', 'hoedspruit', 'chefs', 'foraging', 'lowveld'],
    relatedArticles: [
      { title: 'From Mopane Worms to Rooibos: The Lowveld Flavour Atlas', href: '/story/lowveld-flavours' },
      { title: 'The Town That Tourism Built — and Is Trying Not to Break', href: '/story/hoedspruit-town' },
    ],
    shareUrl: 'https://reporter.marome.io/story/hoedspruit-kitchens',
  },
};

const shangaanBeadworkContent: ThemeProps = {
  hero: {
    title: 'Tsonga Beadwork: Every Colour Has a Conversation',
    subtitle:
      "For Shangaan women in the Kruger borderlands, beadwork is not decoration — it is a living language. Three seasons spent documenting what each pattern says.",
    coverImage: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?auto=format&fit=crop&w=1280&h=720&q=80',
    author: 'Lerato Sithole',
    authorAvatar: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=64&h=64&q=80',
    publishedAt: '2026-05-08T08:00:00Z',
    readingTimeMinutes: 7,
    breadcrumbs: [
      { label: 'Home', href: '/' },
      { label: 'Artisan & Craft', href: '/' },
    ],
  },
  body: {
    html: `<p>The first thing Gogo Hlungwani shows me is a bracelet the width of a hand, built from tiny glass beads in alternating columns of red, white, and black. She does not tell me what it says immediately. She wants to see if I can read it.</p>

<p>I cannot. The language of Tsonga beadwork is not one that outsiders absorb through proximity. It requires transmission — grandmother to granddaughter, aunt to niece — and even among those who have received the transmission, the full grammar is held by fewer and fewer people each generation.</p>

<p>"Red is blood and life," Gogo Hlungwani says eventually, holding the bracelet in the morning light. "White is purity — worn when a young woman has completed initiation. Black is grief, or sometimes power, depending on where it sits in the pattern. But the meaning also changes with the thickness of each column, the number of rows, whether the columns are vertical or diagonal. You see? It is not simple."</p>

<h2 id="a-living-language">A Living Language, Losing Speakers</h2>
<p>Anthropologist Lerato Sithole spent three seasons — winter of 2023, summer of 2024, winter of 2024 — in four communities along the western Kruger borderlands, documenting what she estimates is the last generation of women with full fluency in the beadwork code. Her findings, which will be published later this year in the <em>Journal of Southern African Studies</em>, paint a picture of a communication system of extraordinary sophistication that is in serious danger of being reduced to craft.</p>

<p>"When beadwork becomes decoration, you lose the grammar," Sithole explains. "You can still produce beautiful objects — and people do — but the meaning evaporates. It becomes aesthetic without being semantic. That's a profound loss, not a stylistic change."</p>

<blockquote><p>"A woman's beadwork tells you where she is in her life cycle, who her family is, whether she is available for courtship, whether she is in mourning. It is a curriculum vitae and a diary at the same time."</p></blockquote>

<h2 id="kinship-and-status">Kinship, Status, and Grief</h2>
<p>The system encodes three main categories of information: kinship (which family group a woman belongs to, encoded in specific bead arrangements that are clan-specific), status (her position in the life cycle — girl, initiated woman, married woman, elder), and emotional or social state (mourning, celebration, a dispute in the household, a pregnancy).</p>

<p>A trained reader can determine all of these from a single piece of jewellery. The skill is not decorative; it is communicative in the most functional sense. Historically, it allowed women to convey complex information in contexts where speech was restricted — during initiation ceremonies, in the presence of male elders, or across language barriers between clans.</p>

<h3 id="the-glass-bead-trade">The Glass Bead Trade and Its Legacy</h3>
<p>An irony in the story is that the glass beads themselves are not indigenous. They arrived from Venetian and Bohemian trade routes in the seventeenth and eighteenth centuries, replacing earlier organic materials — seeds, bone, shell — with a more standardised palette. The adoption of glass beads actually expanded the grammar, because the standardised colours and sizes enabled more precise distinctions than the variable organic materials they replaced.</p>

<p>"People sometimes say the beads are colonial objects," Sithole notes. "Which is technically true but misses the point. The women who adopted them made something entirely their own out of the material. The grammar is not Venetian. It is Tsonga."</p>

<h2 id="what-comes-next">What Comes Next</h2>
<p>Gogo Hlungwani has three daughters, two granddaughters, and one great-granddaughter. Of these six women, one daughter and one granddaughter are interested in learning the full code. The others use beadwork socially but have not learned to read it.</p>

<p>"I do not force them," Hlungwani says. "You cannot force a language into someone. But I teach the ones who ask, and I hope that when they are old they will find someone who asks them."</p>

<p>Sithole's documentation project includes video recordings, pattern diagrams, and a digital lexicon that will be held by the communities themselves, not by an academic institution. "The knowledge belongs to them," she says simply. "We are just writing it down so it doesn't have to live only in memory."</p>`,
    tableOfContents: [
      { id: 'a-living-language', label: 'A Living Language', level: 1 },
      { id: 'kinship-and-status', label: 'Kinship, Status, and Grief', level: 1 },
      { id: 'the-glass-bead-trade', label: 'The Glass Bead Trade', level: 2 },
      { id: 'what-comes-next', label: 'What Comes Next', level: 1 },
    ],
  },
  gallery: [
    {
      src: 'https://images.unsplash.com/photo-1602232215399-0d2d2f3bb8ae?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Close-up of Tsonga beadwork bracelet in red, white, and black',
      caption: 'A marriage bracelet from the Thulamela district — the red columns indicate the wearer\'s clan',
      credit: 'Lerato Sithole',
    },
    {
      src: 'https://images.unsplash.com/photo-1524638431109-93d95c968f03?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Gogo Hlungwani at work on a necklace',
      caption: 'Hlungwani threads beads in the sequence her grandmother taught her',
      credit: 'Lerato Sithole',
    },
    {
      src: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Pattern diagrams drawn on kraft paper',
      caption: 'Documentation drawings — the first written records of some patterns',
      credit: 'Lerato Sithole',
    },
    {
      src: 'https://images.unsplash.com/photo-1620503374956-c942862f0372?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Beadwork laid out on a reed mat',
      caption: 'A full set: initiation necklace, mourning band, marriage bracelet, and elder\'s collar',
      credit: 'Lerato Sithole',
    },
    {
      src: 'https://images.unsplash.com/photo-1516534775068-ba3e7458af70?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Young woman learning to read beadwork patterns',
      caption: 'A granddaughter learns to read — not make — the patterns. Reading comes first.',
      credit: 'Lerato Sithole',
    },
    {
      src: 'https://images.unsplash.com/photo-1605792657660-596af9009e82?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Historical beadwork preserved in wax paper',
      caption: 'A piece from 1934, preserved in the family collection — the oldest documented item',
      credit: 'Lerato Sithole',
    },
  ],
  footer: {
    authorName: 'Lerato Sithole',
    authorBio:
      'Dr. Lerato Sithole is a social anthropologist at the University of the Witwatersrand. Her fieldwork focuses on material culture and endangered knowledge systems in southern Africa.',
    authorAvatar: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=64&h=64&q=80',
    tags: ['tsonga', 'beadwork', 'indigenous', 'craft', 'language', 'shangaan', 'limpopo'],
    relatedArticles: [
      { title: "Venda Pottery's Unbroken Thread", href: '/story/venda-pottery' },
      { title: 'The Last Baobab Guardians of Limpopo', href: '/story/baobab-guardians' },
    ],
    shareUrl: 'https://reporter.marome.io/story/shangaan-beadwork',
  },
};

const communityConservanciesContent: ThemeProps = {
  hero: {
    title: 'Who Owns the Wildlife? Community Conservancies Reshape the Map',
    subtitle:
      'Across southern Africa, 22 million hectares have quietly shifted from state parks to community-owned wildlife conservancies in the past decade. The results are complicated — and remarkable.',
    coverImage: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?auto=format&fit=crop&w=1280&h=720&q=80',
    author: 'Sipho Mahlangu',
    authorAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=64&h=64&q=80',
    publishedAt: '2026-05-05T06:00:00Z',
    readingTimeMinutes: 11,
    breadcrumbs: [
      { label: 'Home', href: '/' },
      { label: 'Wildlife & Coexistence', href: '/' },
    ],
  },
  body: {
    html: `<p>In 1994, when Namibia passed the legislation that allowed communal conservancies to form and receive formal rights over wildlife on their land, most conservationists were sceptical. Wildlife management was considered too complex, too capital-intensive, and — a word that would prove ironic — too important to be left to communities.</p>

<p>Thirty years later, Namibia's communal conservancy programme is the most successful wildlife recovery story in Africa. Elephant populations have grown by over 200%. Lion, cheetah, and black rhino have returned to areas where they had been locally extinct for decades. And the communities that manage the conservancies have collectively earned hundreds of millions of rands from sustainable wildlife tourism and harvesting.</p>

<h2 id="the-numbers">The Numbers Are Striking</h2>
<p>As of 2026, 22 million hectares across southern Africa — roughly the combined area of Lesotho, Eswatini, and Mozambique — are under some form of community-managed wildlife authority. This includes 87 communal conservancies in Namibia, 23 community-based natural resource management (CBNRM) zones in Botswana, and a fast-growing cluster of 34 community conservancies along South Africa's former homelands corridor.</p>

<p>The numbers mask enormous variation. Some conservancies are thriving enterprises: professionally managed, ecologically productive, generating meaningful income for hundreds of households. Others are conservancies in name only — registered, but lacking the governance structures, technical support, or economic model to function independently.</p>

<blockquote><p>"A conservancy is not a solution. It is a framework. Whether it works depends entirely on whether the community has genuine authority — over decisions, over income, over who enters and what they do."</p></blockquote>

<h2 id="what-works">What Works — and What Doesn't</h2>
<p>A 2025 meta-analysis by the IUCN and the African Wildlife Foundation examined outcomes across 140 community wildlife management areas in seven southern African countries. The clearest finding: wildlife abundance correlates strongly with community control of revenue, not with the size of the conservancy or the species present.</p>

<p>Where communities control less than 50% of tourism revenue generated on their land, conservation outcomes are statistically indistinguishable from state-managed parks. Where communities control more than 70% of revenue, wildlife abundance increases year-on-year and illegal offtake drops sharply.</p>

<p>"The old model assumed that wildlife needed protection from communities," says Dr. Alinah Setshedi of the Southern African Wildlife College. "The data now suggests the opposite — that the most effective protection is community self-interest, when that self-interest is economically aligned with wildlife survival."</p>

<h3 id="the-south-africa-case">The South African Case: Slower, Messier, More Complicated</h3>
<p>South Africa's community conservancy movement is a generation behind Namibia's, partly because the political landscape is different. Land restitution claims are unresolved on many of the areas most suitable for conservancy formation. Historical distrust between communities and conservation authorities — rooted in the forced removals of the apartheid era — runs deep. And the regulatory environment has been, until recently, hostile to community ownership of wildlife.</p>

<p>The 2023 amendments to the National Environmental Management: Biodiversity Act changed some of this. Communities can now register as legal entities capable of holding wildlife use permits. Several community-conservation partnerships that had previously operated in legal grey areas have formalised under the new framework.</p>

<p>Whether formalisation translates to genuine empowerment is, as yet, unclear. "Legislation is a necessary condition, not a sufficient one," Setshedi notes. "You also need technical support, legal literacy, access to markets, and — critically — time. These things take decades, not seasons."</p>

<h2 id="what-the-wildlife-thinks">What the Wildlife Thinks</h2>
<p>In the Bale Mountains of the South African Lowveld, a community conservancy established six years ago now manages a 48 000-hectare tract that straddles the border between communal land and a private game reserve. Last year, for the first time in recorded history, a pack of African wild dogs denned successfully on the communal side of the fence.</p>

<p>The community's game manager, a 31-year-old named Vusi Ndlovu who grew up herding cattle on the same land, monitored the den with camera traps and kept the location confidential until the pups were mobile. "We didn't want any pressure on them," he says. "Not tourists, not journalists. Just time to be wild."</p>

<p>Six pups survived. The pack now ranges across both the conservancy and the adjacent private reserve — the first wildlife corridor of its kind in the area. For Ndlovu, the dogs' decision to den on the communal land is not a data point. It's something closer to a verdict.</p>

<p>"The animals don't know about ownership," he says. "But they know about safety. If they felt safe enough to have their young here, we must be doing something right."</p>`,
    tableOfContents: [
      { id: 'the-numbers', label: 'The Numbers Are Striking', level: 1 },
      { id: 'what-works', label: 'What Works — and What Doesn\'t', level: 1 },
      { id: 'the-south-africa-case', label: 'The South African Case', level: 2 },
      { id: 'what-the-wildlife-thinks', label: 'What the Wildlife Thinks', level: 1 },
    ],
  },
  gallery: [
    {
      src: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Aerial view of a community conservancy landscape',
      caption: 'The Bale Mountains conservancy — 48 000 hectares of community-managed wilderness',
      credit: 'Sipho Mahlangu',
    },
    {
      src: 'https://images.unsplash.com/photo-1474511320723-9a56873867b5?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'African wild dog pack crossing open ground',
      caption: 'The den pack — six pups born on community land for the first time in recorded history',
      credit: 'Camera trap / Vusi Ndlovu',
    },
    {
      src: 'https://images.unsplash.com/photo-1529539993009-c73dc2b3fc8c?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Community conservancy board meeting',
      caption: 'Monthly governance meeting — attendance and agenda are published to all households',
      credit: 'Sipho Mahlangu',
    },
    {
      src: 'https://images.unsplash.com/photo-1567427017947-545c5f8d16ad?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Revenue distribution ceremony',
      caption: 'Annual wildlife revenue distribution — R2.4m shared across 340 member households',
      credit: 'Sipho Mahlangu',
    },
  ],
  footer: {
    authorName: 'Sipho Mahlangu',
    authorBio:
      'Sipho Mahlangu is an investigative journalist covering land rights and conservation governance in southern Africa. He is a fellow of the African Investigative Journalism Conference.',
    authorAvatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&w=64&h=64&q=80',
    tags: ['conservancy', 'community', 'wildlife', 'land-rights', 'namibia', 'south-africa'],
    relatedArticles: [
      { title: 'The Last Baobab Guardians of Limpopo', href: '/story/baobab-guardians' },
      { title: "Forty Years of Watching Elephants Remember", href: '/story/elephant-memory' },
    ],
    shareUrl: 'https://reporter.marome.io/story/community-conservancies',
  },
};

const hoedspruitTownContent: ThemeProps = {
  hero: {
    title: 'The Town That Tourism Built — and Is Trying Not to Break',
    subtitle:
      "Hoedspruit, population 8 000, now hosts more than 400 000 visitors a year. The locals who grew up here are navigating what it means to live inside someone else's idea of paradise.",
    coverImage: 'https://images.unsplash.com/photo-1532375810709-75b1da00537c?auto=format&fit=crop&w=1280&h=720&q=80',
    author: 'Amara Nkosi',
    authorAvatar: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=64&h=64&q=80',
    publishedAt: '2026-05-12T07:30:00Z',
    readingTimeMinutes: 8,
    breadcrumbs: [
      { label: 'Home', href: '/' },
      { label: 'Experiences', href: '/' },
    ],
  },
  body: {
    html: `<p>The road into Hoedspruit from the south passes a petrol station, a Spar, a Pick n Pay that opened last year, and approximately fourteen lodges advertising proximity to the Big Five before you reach anything that looks like it was built for people who actually live here. Then there is the post office, two schools, a clinic, and the small informal market where Paulina Mathebula sells avocados from her garden every Thursday morning.</p>

<p>Mathebula has lived in Hoedspruit for 53 years. She watched it expand from a sleepy farming service town into something she does not have a good word for in Sepedi, and the English words available — tourism hub, gateway town, hotspot — all feel like they belong to the visitors, not to her.</p>

<h2 id="the-numbers">400 000 Visitors, 8 000 Residents</h2>
<p>The numbers tell the story baldly: Hoedspruit receives more than 50 visitors for every permanent resident each year. The majority of those visitors pass through the town on the way to the reserves, spending money at the petrol station and the supermarket and then disappearing into the private game sector, where the economic infrastructure is separate from the town's.</p>

<p>Tourism income in the greater Hoedspruit area was estimated at R3.2 billion in 2025. The town's municipal budget for the same year was R180 million. A small fraction of the tourism spend flows into municipal coffers through rates, levies, and the tourism tax introduced in 2023. The rest — the accommodation revenue, the safari fees, the wine-list margins at the lodge restaurants — stays in the private sector.</p>

<blockquote><p>"They talk about Hoedspruit as if it's a destination. But for us it's a place where we go to school and buy groceries and try to get a doctor's appointment. We have been living in a destination for 30 years and we are exhausted."</p></blockquote>

<h2 id="housing">The Housing Crisis No One Photographs</h2>
<p>The most urgent issue in Hoedspruit is housing. Property prices in the town and its immediate surroundings have tripled in the past decade, driven partly by the same demand that fills the lodges — wealthy domestic and international buyers acquiring plots and smallholdings for "lifestyle" purposes. Workers in the lodge sector commute increasingly long distances as the townships adjacent to the town fill and overflow.</p>

<p>A teacher at the local primary school earns R22 000 a month. A double room in the cheapest available rental in town costs R8 500. The arithmetic is not complicated, and neither are its consequences: the people who run the schools, the clinic, and the roads live progressively further from the town they maintain.</p>

<h3 id="what-locals-want">What the Locals Want</h3>
<p>In a series of interviews conducted over two weeks in Hoedspruit and the surrounding townships, the same things kept coming up: better public transport (the bus service was cut in 2021), a functioning public swimming pool, more local enterprise support for residents who want to build tourism businesses of their own rather than working for someone else's.</p>

<p>Several residents mentioned the traffic — not as a complaint about tourists specifically, but as a symptom of the town's growth outrunning its infrastructure. The main road through Hoedspruit was designed for a farming service town. It was not designed for 400 000 annual visitors, and there is currently no funded plan to widen it.</p>

<h2 id="accommodation-to-identity">From Accommodation to Identity</h2>
<p>Mathebula is not anti-tourism. She rents two rooms of her house to travellers twice a month through a local homestay network, and she describes the experience as mostly positive. What she objects to is the way the town's identity has been captured by an economy she has almost no ownership stake in.</p>

<p>"I was born here. My children were born here. But when I read about Hoedspruit in a magazine, I don't recognize the place they're describing. They're writing about sunsets and elephant sightings. We are writing about school fees and taxi fares." She pauses. "Both things are real. But only one of them gets published."</p>

<p>The town's newly formed Resident Advocacy Forum has begun meeting with the municipal council to negotiate a community benefit agreement with the larger lodges — a structured mechanism to direct a portion of tourism revenue toward town infrastructure. It is early days. The lodges are listening; they are not yet committing.</p>

<p>"Progress," Mathebula says, when I ask about the meetings. "Slow progress. Which is better than no progress, I suppose. But I have been waiting 30 years, so my patience is calibrated differently than theirs."</p>`,
    tableOfContents: [
      { id: 'the-numbers', label: '400 000 Visitors, 8 000 Residents', level: 1 },
      { id: 'housing', label: 'The Housing Crisis', level: 1 },
      { id: 'what-locals-want', label: 'What the Locals Want', level: 2 },
      { id: 'accommodation-to-identity', label: 'From Accommodation to Identity', level: 1 },
    ],
  },
  gallery: [
    {
      src: 'https://images.unsplash.com/photo-1532375810709-75b1da00537c?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Main street in Hoedspruit at midday',
      caption: 'The main road was designed for a farming town. It was not designed for this.',
      credit: 'Amara Nkosi',
    },
    {
      src: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Paulina Mathebula at her market stall',
      caption: 'Mathebula has sold avocados at the Thursday market for 28 years',
      credit: 'Amara Nkosi',
    },
    {
      src: 'https://images.unsplash.com/photo-1503387837-b154d5074bd2?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Construction of a new lodge visible from the township',
      caption: 'A lodge under construction on the edge of town — the sixth to open in three years',
      credit: 'Amara Nkosi',
    },
  ],
  footer: {
    authorName: 'Amara Nkosi',
    authorBio:
      'Amara Nkosi covers urban sociology and the economics of tourism in southern Africa for Reporter. She is based in Johannesburg.',
    authorAvatar: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=64&h=64&q=80',
    tags: ['hoedspruit', 'tourism', 'housing', 'limpopo', 'community', 'development'],
    relatedArticles: [
      { title: "Hoedspruit's Bush Kitchens Are Rewriting Safari Dining", href: '/story/hoedspruit-kitchens' },
      { title: 'Who Owns the Wildlife? Community Conservancies Reshape the Map', href: '/story/community-conservancies' },
    ],
    shareUrl: 'https://reporter.marome.io/story/hoedspruit-town',
  },
};

const lowveldFlavoursContent: ThemeProps = {
  hero: {
    title: 'From Mopane Worms to Rooibos: The Lowveld Flavour Atlas',
    subtitle:
      'A researcher and a chef drive 2 400 km across the Lowveld documenting 118 indigenous ingredients that rarely appear on any restaurant menu — and almost never leave the region.',
    coverImage: 'https://images.unsplash.com/photo-1554830072-52d78d0d4c18?auto=format&fit=crop&w=1280&h=720&q=80',
    author: 'Dr. Zanele Mokoena',
    authorAvatar: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=64&h=64&q=80',
    publishedAt: '2026-05-03T06:00:00Z',
    readingTimeMinutes: 10,
    breadcrumbs: [
      { label: 'Home', href: '/' },
      { label: 'Culinary Journeys', href: '/' },
    ],
  },
  body: {
    html: `<p>We start in Louis Trichardt on a Tuesday in March, when the mango trees are still heavy and the air smells of fermenting fruit. By Friday we will be in Komatipoort, 480 km southeast, where the heat is different — wetter, thicker, the vegetation tangled and tropical. Between these two points, we will eat more unfamiliar things than I have eaten in the previous five years combined.</p>

<p>Chef Kagiso Sithole drives. I document. Our shared project — the Lowveld Flavour Atlas — began as a conversation at a food symposium in Cape Town where we both sat through a panel on "South African cuisine" that mentioned exactly zero ingredients indigenous to the Lowveld. We decided to build the missing map ourselves.</p>

<h2 id="what-we-found">What We Found: 118 Ingredients</h2>
<p>The final count, after two years of fieldwork across eleven months, is 118 distinct edible plant and animal species that are regularly used for food or medicine in Lowveld communities but are virtually absent from commercial food culture. The list ranges from the famous (mopane worms, marula, sour figs) to the obscure (the dried flowers of the <em>Hypoxis</em> plant, used as a soup base in some Venda communities; the fermented seed paste of the jackalberry tree, which tastes, disconcertingly, like a mild blue cheese).</p>

<p>What the 118 ingredients have in common is geography and invisibility. They are part of daily life for millions of people in the Limpopo and Mpumalanga lowveld. They are almost entirely unknown outside it.</p>

<blockquote><p>"The same country that exports a billion rands' worth of macadamia nuts and avocados also has 118 remarkable ingredients that nobody outside 200 kilometres has ever heard of. That is not a culinary gap. It is an economic one."</p></blockquote>

<h2 id="timeline-of-discovery">A Timeline of Tastes</h2>
<p><strong>Day 1, Louis Trichardt:</strong> We visit a community market on the edge of town where three women sell dried mopane worms in woven baskets. The worms, dried and sometimes smoked, have a nutty, slightly mushroomy flavour that Sithole immediately identifies as umami-forward. "These could anchor a broth the way kombu does in Japan," he says. "Nobody is doing that."</p>

<p><strong>Day 3, near Thohoyandou:</strong> A retired schoolteacher named Mavis introduces us to <em>mutbvumbu</em>, a fermented maize porridge that has been sitting in a clay pot for four days. The flavour is sour, complex, and oddly refreshing — closer to a drinkable yoghurt than anything I expected. Mavis adds dried wild garlic and a pinch of something she calls "the spice" — which turns out to be ground dried caterpillar mixed with smoked chilli. It is extraordinary.</p>

<p><strong>Day 5, near Phalaborwa:</strong> The jackalberry discovery. We are shown the paste by a man named Elias, who has been making it for forty years from his grandfather's recipe. The fermentation process takes three weeks, uses only wild yeast, and produces a flavour that Sithole describes as "what happens when Africa invents its own cheese." We spend three hours learning the process, which Elias has never written down.</p>

<p><strong>Day 8, Komatipoort:</strong> Marula beer, made from fruit that fell two days ago. Light, effervescent, faintly citrusy, alcoholic but not heavily so. The brewer, who asks not to be named, tells us she produces around 400 litres per marula season and sells it all locally. "The tourists don't know it exists," she says. "Which is fine. It's not for them."</p>

<h2 id="the-atlas">What the Atlas Will Do</h2>
<p>The Lowveld Flavour Atlas will be published in partnership with three universities, the Limpopo Department of Agriculture, and a food-system NGO. It will include photographs, collection methods, preparation techniques, nutritional analysis, and — critically — the communities from which each ingredient comes, so that any commercial use is traced back to its source.</p>

<p>The intellectual property framework is the most contentious part of the project. Some communities are enthusiastic about having their food knowledge documented and potentially commercialised. Others are concerned about losing control of ingredients they have managed for generations. We have tried to build the framework so that communities retain veto rights over commercial applications.</p>

<p>"This is not a cookbook," Sithole says. "It is a land survey. We are mapping what exists before it disappears — and making the case that it is worth keeping."</p>

<p>Of the 118 ingredients we documented, fifteen are under ecological stress due to over-harvesting or habitat loss. Four are critically endangered in their natural range. Without documentation, and without the economic incentives that recognition creates, they may not survive the decade. The atlas is, among other things, a conservation argument. The best way to protect a food plant, it turns out, is to make it valuable.</p>`,
    tableOfContents: [
      { id: 'what-we-found', label: 'What We Found: 118 Ingredients', level: 1 },
      { id: 'timeline-of-discovery', label: 'A Timeline of Tastes', level: 1 },
      { id: 'the-atlas', label: 'What the Atlas Will Do', level: 1 },
    ],
  },
  gallery: [
    {
      src: 'https://images.unsplash.com/photo-1559181567-c3190958d3b5?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Dried mopane worms in a woven basket',
      caption: 'Mopane worms — sun-dried and sometimes smoked, with a nutty umami depth',
      credit: 'Dr. Zanele Mokoena',
    },
    {
      src: 'https://images.unsplash.com/photo-1559181567-c3190958d3b5?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Marula fruit on the tree',
      caption: 'Marula ripens January–March — the window for fresh juice, beer, and oil is short',
      credit: 'Dr. Zanele Mokoena',
    },
    {
      src: 'https://images.unsplash.com/photo-1466637574441-749b8f19452f?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Wild herbs laid on a wooden table for documentation',
      caption: 'Day 6: 22 specimens laid out for photography and nutritional sampling',
      credit: 'Kagiso Sithole',
    },
    {
      src: 'https://images.unsplash.com/photo-1565557623262-b51c2513a641?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Clay pot of fermented maize porridge',
      caption: 'Mutbvumbu fermenting in a clay pot — four days produces the right sourness',
      credit: 'Dr. Zanele Mokoena',
    },
  ],
  footer: {
    authorName: 'Dr. Zanele Mokoena',
    authorBio:
      'Dr. Zanele Mokoena is an ethno-botanist and food systems researcher at the University of Limpopo. The Lowveld Flavour Atlas is due for publication in late 2026.',
    authorAvatar: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=64&h=64&q=80',
    tags: ['food', 'indigenous', 'lowveld', 'flavour', 'mopane', 'marula', 'research'],
    relatedArticles: [
      { title: "Hoedspruit's Bush Kitchens Are Rewriting Safari Dining", href: '/story/hoedspruit-kitchens' },
      { title: 'The Last Baobab Guardians of Limpopo', href: '/story/baobab-guardians' },
    ],
    shareUrl: 'https://reporter.marome.io/story/lowveld-flavours',
  },
};

const georgeRoadContent: ThemeProps = {
  hero: {
    title: 'The Road from George: Slow Travel on the Garden Route',
    subtitle:
      'Forget the N2. A travel writer takes the forgotten back roads from George to Knysna on foot and bicycle, staying with farming families and finding the coast South Africa forgot to Instagram.',
    coverImage: 'https://images.unsplash.com/photo-1560347876-aeef00932037?auto=format&fit=crop&w=1280&h=720&q=80',
    author: 'Pieter van der Berg',
    authorAvatar: 'https://images.unsplash.com/photo-1553530979-9b78e643cef0?auto=format&fit=crop&w=64&h=64&q=80',
    publishedAt: '2026-05-07T08:00:00Z',
    readingTimeMinutes: 7,
    breadcrumbs: [
      { label: 'Home', href: '/' },
      { label: 'Experiences', href: '/' },
    ],
  },
  body: {
    html: `<p>The Garden Route is South Africa's most photographed coastline and, for anyone who has driven the N2 in January, also its most reliably frustrating road. Caravans, camper vans, hire cars doing 80 in a 120 zone — the route has been so thoroughly colonised by tourism infrastructure that it can be difficult to remember there is still a place underneath it.</p>

<p>I decided to find it. Not by going off-season, not by driving faster, but by not driving at all.</p>

<h2 id="the-route">The Route: 87 km, Eight Days</h2>
<p>George to Knysna by the back roads is 87 kilometres. On foot and bicycle it took me eight days. I carried a 14-kilogram pack, a folding bicycle, and an embarrassingly optimistic set of assumptions about how much ground I would cover before dark.</p>

<p>The first two days were the hardest. The Outeniqua mountains rise almost immediately behind George, and the farm roads that cross them are loose-gravel affairs that have not been graded since the last election promise about rural infrastructure. My bicycle was, on these sections, more of a very expensive wheelbarrow. I pushed it uphill and held on descending.</p>

<p>What I gained from the difficulty was access. Farming families who would be invisible from the N2 — small-scale vegetable growers, a lavender farm run by a retired engineer, a family that has been making cheese for three generations in a valley with no cell signal — became my accommodation and my context.</p>

<blockquote><p>"We get almost no visitors," said the cheese farmer, a man named Willem, on my third evening. "People drive past this valley every day on the N2 and they don't know we exist. Which is mostly fine, but sometimes I think — all that beauty, and nobody's looking."</p></blockquote>

<h2 id="what-the-slow-road-shows">What the Slow Road Shows</h2>
<p>The Garden Route's received reputation is coastal — forests, bays, white beaches, the Knysna lagoon. What you find on the back roads is a different ecosystem: the fynbos-agriculture transition zone, where protea fields give way to apple orchards and then to the mixed scrub that fills the valleys between the coastal ridge and the mountains. It is not dramatic. It is not photogenic in the Instagram sense. It is persistently, quietly beautiful.</p>

<p>On day five I cycled for six hours without seeing another tourist. I passed three farmworkers on a tractor, a border collie herding sheep along a track, and a woman standing in her garden who waved at me with the unhurried friendliness of someone who waves at everything that passes because almost nothing does.</p>

<h3 id="the-coast-they-forgot">The Coast They Forgot to Instagram</h3>
<p>There is a small beach — I won't name it, not out of exclusivity but out of respect for the family that has been having Sunday braais there for forty years — that you can reach only by a farm track and a 20-minute walk down an eroding cliff path. It has no facilities, no naming rights, no Instagram geotag. It has a stream, a small lagoon, and a view of the Heads that is, arguably, better than the famous one.</p>

<p>I sat there for two hours. I was alone the entire time. This is the part of the Garden Route that the marketing doesn't know about, partly because it can't be monetised and partly because the people who know about it have correctly identified that telling too many people would change it.</p>

<h2 id="back-to-george">The Return</h2>
<p>I took the N2 back from Knysna, in a hire car, and it took 58 minutes. The road I had walked and cycled for eight days compressed into less than an hour. The farms were invisible from the highway. Willem's cheese valley was not marked on the map.</p>

<p>This, I think, is not a failure of the road but of the way we frame travel. We design routes around throughput and landmarks. We measure journeys in kilometres per hour. The Garden Route is a spectacular place when you move through it at 120 km/h. It is a different, more complicated, more interesting place when you move through it at 7.</p>`,
    tableOfContents: [
      { id: 'the-route', label: 'The Route: 87 km, Eight Days', level: 1 },
      { id: 'what-the-slow-road-shows', label: 'What the Slow Road Shows', level: 1 },
      { id: 'the-coast-they-forgot', label: 'The Coast They Forgot', level: 2 },
      { id: 'back-to-george', label: 'The Return', level: 1 },
    ],
  },
  gallery: [
    {
      src: 'https://images.unsplash.com/photo-1501854140801-50d01698950b?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Gravel farm road through the Outeniqua foothills',
      caption: 'Day 2: the back roads have not been graded since the last election promise',
      credit: 'Pieter van der Berg',
    },
    {
      src: 'https://images.unsplash.com/photo-1499002238440-d264edd596ec?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Lavender farm in a mountain valley',
      caption: 'The lavender farm at kilometer 34 — retired engineer, no passing trade, no complaints',
      credit: 'Pieter van der Berg',
    },
    {
      src: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Hidden beach with view of the Knysna Heads',
      caption: 'The beach I won\'t name — two hours, complete solitude',
      credit: 'Pieter van der Berg',
    },
  ],
  footer: {
    authorName: 'Pieter van der Berg',
    authorBio:
      'Pieter van der Berg is a travel writer and slow-travel advocate based in Cape Town. His book <em>The Wrong Road</em> is published by Penguin Random House.',
    authorAvatar: 'https://images.unsplash.com/photo-1553530979-9b78e643cef0?auto=format&fit=crop&w=64&h=64&q=80',
    tags: ['garden-route', 'slow-travel', 'george', 'knysna', 'south-africa', 'cycling'],
    relatedArticles: [
      { title: 'The Town That Tourism Built — and Is Trying Not to Break', href: '/story/hoedspruit-town' },
      { title: "Hoedspruit's Bush Kitchens Are Rewriting Safari Dining", href: '/story/hoedspruit-kitchens' },
    ],
    shareUrl: 'https://reporter.marome.io/story/george-road',
  },
};

const elephantMemoryContent: ThemeProps = {
  hero: {
    title: 'Forty Years of Watching Elephants Remember',
    subtitle:
      "Researcher Cynthia Masondo has tracked the same Kruger herd since 1986. What they've taught her about grief, decision-making, and matriarchal leadership has reshaped conservation science.",
    coverImage: 'https://images.unsplash.com/photo-1564760055775-d63b17a55c44?auto=format&fit=crop&w=1280&h=720&q=80',
    author: 'Cynthia Masondo',
    authorAvatar: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=64&h=64&q=80',
    publishedAt: '2026-05-18T06:00:00Z',
    readingTimeMinutes: 12,
    breadcrumbs: [
      { label: 'Home', href: '/' },
      { label: 'People & Culture', href: '/' },
    ],
  },
  body: {
    html: `<p>The first time I saw her, she was standing in the Letaba River with water at her knees, facing upstream, doing nothing I could name. She was old even then — the deep forehead wrinkles, the ears mapped with veins, the way she carried herself with the particular gravity of something that has been on the earth for a very long time. I named her Thembi, which means "hope" in Zulu, and I have been watching her ever since.</p>

<p>That was September 1986. I was 24 years old, finishing my MSc, and planned to spend two years on a behavioural study before moving on to a university career in Johannesburg. Forty years later I am still here. Thembi is still alive. And the herd — which numbered 23 when I first encountered them — has grown, contracted through drought and disease, split, reunited, and now stands at 31 individuals across three generations.</p>

<h2 id="what-memory-means">What Memory Means to an Elephant</h2>
<p>The popular image of elephant memory — the phrase "an elephant never forgets" — is accurate in ways that science has only recently been able to quantify. Elephants remember individual faces and voices for decades. They remember the locations of water sources they have not visited in years. They remember and appear to grieve over their dead, returning to bones and touching them with their trunks.</p>

<p>What the popular image misses is the social function of that memory. Elephant memory is not a private asset. It is a community infrastructure. A matriarch's knowledge of drought routes, predator territories, and the locations of seasonal food sources is not encoded in any individual — it is held, enacted, and transmitted through the herd's movement patterns and social structure.</p>

<blockquote><p>"When we lose a matriarch, we don't lose one elephant. We lose the herd's collective GPS — decades of navigational and ecological knowledge that cannot be recovered, only slowly rebuilt."</p></blockquote>

<h2 id="thembi-as-teacher">Thembi as Teacher</h2>
<p>Thembi became the herd's matriarch in 1998, when her mother Ntombi died of a suspected infection. She was 36 — young for the role. In the years that followed I watched her make decisions that seemed, at the time, inexplicable: routing the herd away from a water source that had been reliable for years, only for that source to dry up three weeks later; steering clear of a valley where she had not been in eleven years, a valley where we later found evidence of recent lion activity.</p>

<p>How did she know? The most honest answer is that we don't fully understand. The best hypothesis, supported by the work of Dr. Karen McComb at the University of Sussex, is that matriarchs build a threat-detection model from decades of accumulated experience — a sort of internal risk map that integrates sensory data (smell, subsonic vibration, the alarm calls of other species) with memory at a level of sophistication that our instruments can observe but not yet explain.</p>

<h3 id="grief">On the Question of Grief</h3>
<p>The most contested area of elephant cognition research is grief. The scientific consensus has shifted significantly in the past twenty years: it is now well established that elephants display behaviours associated with mourning — prolonged attendance at carcasses, touching of bones, social withdrawal following a death. What remains debated is whether these behaviours constitute an emotional experience analogous to human grief.</p>

<p>I have watched Thembi stand over Ntombi's bones on four occasions over the past 26 years, always at the end of the dry season when the herd passes through that area. Each time, she stays longer than the others. Each time, she touches the skull. I do not know what that means to her. I have written about it in clinical language in six papers. In my journal, which no one will read, I have written about it differently.</p>

<h2 id="the-calves">The Calves: Three Generations</h2>
<p>Thembi has had seven calves in the 40 years I have known her. Five are alive. Her eldest daughter, whom I call Nandi (after the Zulu queen), is now 35 and is beginning to show the behaviours that characterise an apprentice matriarch — spending more time near the herd's decision points, watching Thembi's navigational choices, integrating into the conflict-resolution dynamics that keep the herd's social structure intact.</p>

<p>Nandi's eldest calf, a male who is now 8, has already begun demonstrating what researchers call "musth awareness" — understanding his own hormonal cycles and moderating his behaviour in response. He spends more time at the herd's periphery than his female cousins. He returns, unfailingly, to Thembi's proximity during social stress events. The attachment appears to be mutual.</p>

<h2 id="what-i-have-learned">What Forty Years Teaches</h2>
<p>I am often asked what I have learned. The honest answer is that I have learned to be less certain. The longer I watch, the more I understand that the models we build — cognitive maps, threat-detection systems, social hierarchy frameworks — are approximations that the elephants are under no obligation to honour.</p>

<p>What I can say is this: elephants are not simpler than we are. They are differently complex. Their social lives involve memory, politics, grief, pedagogy, and what I can only call, at the risk of my scientific credibility, wisdom. Not instinct dressed as wisdom. Wisdom: the application of experience to circumstances that were not anticipated.</p>

<p>Thembi is 74. Elephants in the wild rarely survive past 65 — their teeth wear down and they can no longer process food. She is still moving, still navigating, still at the centre of a herd that consults her, follows her, and has no apparent framework for imagining life without her.</p>

<p>Neither, after 40 years, do I.</p>`,
    tableOfContents: [
      { id: 'what-memory-means', label: 'What Memory Means to an Elephant', level: 1 },
      { id: 'thembi-as-teacher', label: 'Thembi as Teacher', level: 1 },
      { id: 'grief', label: 'On the Question of Grief', level: 2 },
      { id: 'the-calves', label: 'The Calves: Three Generations', level: 1 },
      { id: 'what-i-have-learned', label: 'What Forty Years Teaches', level: 1 },
    ],
  },
  gallery: [
    {
      src: 'https://images.unsplash.com/photo-1564760055775-d63b17a55c44?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Thembi standing at the Letaba River',
      caption: 'Thembi at the Letaba — the location of our first encounter, September 1986',
      credit: 'Cynthia Masondo',
    },
    {
      src: 'https://images.unsplash.com/photo-1504173010664-32509aeebb62?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Herd crossing dry grassland at dusk',
      caption: 'The herd moving at dusk — Thembi leads, Nandi flanks, the youngest calves in the centre',
      credit: 'Cynthia Masondo',
    },
    {
      src: 'https://images.unsplash.com/photo-1521651201144-634f700b36ef?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Young elephant calf nursing',
      caption: "Nandi's youngest — 14 months old, already learning the routes",
      credit: 'Cynthia Masondo',
    },
    {
      src: 'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Researcher with field notebooks',
      caption: '40 years of field journals — 87 notebooks, approximately 3 million words',
      credit: 'Field station archive',
    },
  ],
  footer: {
    authorName: 'Cynthia Masondo',
    authorBio:
      'Dr. Cynthia Masondo is a principal researcher at the South African National Biodiversity Institute and a research associate of the Elephant Voices Project. She has published over 60 papers on elephant cognition and social behaviour.',
    authorAvatar: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=64&h=64&q=80',
    tags: ['elephants', 'kruger', 'cognition', 'conservation', 'matriarch', 'memory', 'wildlife'],
    relatedArticles: [
      { title: 'Who Owns the Wildlife? Community Conservancies Reshape the Map', href: '/story/community-conservancies' },
      { title: 'The Last Baobab Guardians of Limpopo', href: '/story/baobab-guardians' },
      { title: 'Tsonga Beadwork: Every Colour Has a Conversation', href: '/story/shangaan-beadwork' },
    ],
    shareUrl: 'https://reporter.marome.io/story/elephant-memory',
  },
};

const vendaPotteryContent: ThemeProps = {
  hero: {
    title: "Venda Pottery's Unbroken Thread",
    subtitle:
      "In Limpopo's Venda region, clay pots are still made the same way they were a thousand years ago. Three women from the same family show what continuity looks like — and what it costs.",
    coverImage: 'https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?auto=format&fit=crop&w=1280&h=720&q=80',
    author: 'Faith Mukhuba',
    authorAvatar: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=64&h=64&q=80',
    publishedAt: '2026-05-20T07:00:00Z',
    readingTimeMinutes: 6,
    breadcrumbs: [
      { label: 'Home', href: '/' },
      { label: 'Heritage & Traditions', href: '/' },
    ],
  },
  body: {
    html: `<p>The following is an edited transcript of three conversations conducted over two days in the village of Tshikundamalema, Limpopo, with Gogo Mukhuba (82), her daughter Mulalo (54), and her granddaughter Zwivhuya (28). The three women represent a continuous lineage of Venda coil pottery — one of the oldest unbroken ceramic traditions in sub-Saharan Africa.</p>

<p><em>Reporter:</em> Gogo, how old were you when you learned?</p>
<p><em>Gogo Mukhuba:</em> I was six. My mother put clay in my hands before I could read. The hands learn before the mind does. That is the Venda way — the body holds the knowledge first, the understanding comes later.</p>

<p><em>Reporter:</em> Can you describe the process for someone who has never seen it?</p>
<p><em>Gogo Mukhuba:</em> You start with the clay, which we dig from one place only. The same place my grandmother dug from, and her grandmother before her. The clay from that ground has a quality — a smoothness, a memory in it — that clay from other places does not have. You mix it with water and you leave it to rest. Then you begin coiling. You roll thin ropes of clay between your palms and you build the pot from the base upward, coil by coil. You join each coil to the one before by pressing with your thumb — not too hard, not too gently. The pressure is everything. A pot is as much the spaces between the coils as the clay itself.</p>

<h2 id="what-is-being-kept">What Is Being Kept</h2>
<p><em>Mulalo:</em> The technique hasn't changed. We don't use a wheel. We don't use a kiln — we fire in an open pit, the same way it has always been done. The wood we use matters too. We use the wood of specific trees, which burn at a certain temperature that gives the clay its colour.</p>

<p><em>Reporter:</em> What kind of colour?</p>
<p><em>Mulalo:</em> A brown-black. Not matte, not shiny — something between. You can't get it any other way. You can try with a kiln. I have tried. The pots are technically fine but they look like they came from nowhere. They have no address.</p>

<blockquote><p>"Every pot we make is made the same way as the one made a thousand years ago. That is not stubbornness. That is love."</p></blockquote>

<h2 id="what-is-being-lost">What Is Being Lost</h2>
<p><em>Zwivhuya:</em> I'm the one who sells on Instagram. Gogo doesn't even have a phone. I take photographs and post them and the pots go to people in Johannesburg, Cape Town, London. That's good — it means we can keep doing this, because the money comes in. But I also notice what people buy. They buy the beautiful ones. The ceremonial ones. They don't buy the everyday pots — the cooking pots, the water storage pots — because they have plastic containers from Checkers.</p>

<p><em>Reporter:</em> Is that a problem?</p>
<p><em>Zwivhuya:</em> It means the everyday pots are dying. They were the foundation of the tradition — the things you made every week because you needed them. The ceremonial pots were built on top of that. If you only make the top and not the foundation, I'm not sure what you have left.</p>

<p><em>Mulalo:</em> She is right about this. But she is also still here, which means something.</p>

<h2 id="what-comes-next">The Next Generation</h2>
<p><em>Reporter:</em> Zwivhuya, do you want to continue?</p>
<p><em>Zwivhuya:</em> I studied accounting in Thohoyandou. I came back because my grandmother is old and she asked me to. I stayed because — I don't know how to explain it. When I am making a pot, I am not in the room I'm in. I am in a room that contains all the women who did this before me. It is a very full room.</p>

<p><em>Reporter:</em> Gogo, what do you want people to understand about this work?</p>
<p><em>Gogo Mukhuba:</em> That a pot is not an object. It is a decision — made by many hands, across many generations — to keep something going. Every pot we make is made the same way as the one made a thousand years ago. That is not stubbornness. That is love.</p>`,
    tableOfContents: [
      { id: 'what-is-being-kept', label: 'What Is Being Kept', level: 1 },
      { id: 'what-is-being-lost', label: 'What Is Being Lost', level: 1 },
      { id: 'what-comes-next', label: 'The Next Generation', level: 1 },
    ],
  },
  gallery: [
    {
      src: 'https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Gogo Mukhuba coiling clay',
      caption: 'Gogo at work — the same technique her grandmother taught her at age six',
      credit: 'Faith Mukhuba',
    },
    {
      src: 'https://images.unsplash.com/photo-1493106641515-5a4ae80adf90?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Finished Venda pots drying in sunlight',
      caption: 'Drying before the open-pit firing — two days in direct sun',
      credit: 'Faith Mukhuba',
    },
    {
      src: 'https://images.unsplash.com/photo-1608155686393-8fdd966d784b?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Open pit firing with wood smoke',
      caption: 'The firing — specific wood species, specific temperature, the colour cannot be replicated by kiln',
      credit: 'Faith Mukhuba',
    },
    {
      src: 'https://images.unsplash.com/photo-1541845157-a6d2d100c931?auto=format&fit=crop&w=800&h=600&q=80',
      alt: 'Three generations of potters together',
      caption: 'Gogo Mukhuba, Mulalo, and Zwivhuya — three generations, one unbroken thread',
      credit: 'Faith Mukhuba',
    },
  ],
  footer: {
    authorName: 'Faith Mukhuba',
    authorBio:
      'Faith Mukhuba is a journalist and documentary photographer from the Venda region of Limpopo. She covers indigenous arts and cultural heritage across sub-Saharan Africa.',
    authorAvatar: 'https://images.unsplash.com/photo-1531746790731-6c087fecd65a?auto=format&fit=crop&w=64&h=64&q=80',
    tags: ['venda', 'pottery', 'limpopo', 'craft', 'heritage', 'ceramic', 'tradition'],
    relatedArticles: [
      { title: 'Tsonga Beadwork: Every Colour Has a Conversation', href: '/story/shangaan-beadwork' },
      { title: 'The Last Baobab Guardians of Limpopo', href: '/story/baobab-guardians' },
    ],
    shareUrl: 'https://reporter.marome.io/story/venda-pottery',
  },
};

// ---------------------------------------------------------------------------
// Full article list — each showcasing a different Reporter theme
// ---------------------------------------------------------------------------

export const ARTICLES: Article[] = [
  {
    id: 'baobab-guardians',
    title: 'The Last Baobab Guardians of Limpopo',
    excerpt:
      'In the far northern reaches of Limpopo, a small community of San descendants has tended the same grove of ancient baobabs for eleven generations. Now, as climate patterns shift and commercial pressures mount, they are racing to document every tree before the knowledge disappears.',
    category: 'Wildlife & Coexistence',
    author: 'Nomvula Dlamini',
    readTime: 9,
    featured: true,
    gradientFrom: '#064e3b',
    gradientTo: '#78350f',
    theme: 'scrolling-long-form',
    content: baobabContent,
  },
  {
    id: 'hoedspruit-kitchens',
    title: "Hoedspruit's Bush Kitchens Are Rewriting Safari Dining",
    excerpt:
      'A new generation of chefs is swapping hotel banquet menus for fire pits and foraged marula, building hyperlocal tasting experiences that rival anything in Cape Town.',
    category: 'Culinary Journeys',
    author: 'Thabo Khumalo',
    readTime: 6,
    gradientFrom: '#7c2d12',
    gradientTo: '#1c1917',
    theme: 'magazine-grid',
    content: hoedspruitKitchensContent,
  },
  {
    id: 'shangaan-beadwork',
    title: 'Tsonga Beadwork: Every Colour Has a Conversation',
    excerpt:
      'For Shangaan women in the Kruger borderlands, beadwork is not decoration — it is a living language. Anthropologist Lerato Sithole spent three seasons documenting what each pattern communicates about kinship, status, and grief.',
    category: 'Artisan & Craft',
    author: 'Lerato Sithole',
    readTime: 7,
    gradientFrom: '#581c87',
    gradientTo: '#0c4a6e',
    theme: 'photo-essay',
    content: shangaanBeadworkContent,
  },
  {
    id: 'community-conservancies',
    title: 'Who Owns the Wildlife? Community Conservancies Reshape the Map',
    excerpt:
      'Across southern Africa, 22 million hectares have quietly shifted from state parks to community-owned wildlife conservancies in the past decade. The results are complicated — and remarkable.',
    category: 'Wildlife & Coexistence',
    author: 'Sipho Mahlangu',
    readTime: 11,
    gradientFrom: '#14532d',
    gradientTo: '#052e16',
    theme: 'data-viz',
    content: communityConservanciesContent,
  },
  {
    id: 'hoedspruit-town',
    title: 'The Town That Tourism Built — and Is Trying Not to Break',
    excerpt:
      "Hoedspruit, population 8 000, now hosts more than 400 000 visitors a year. The locals who grew up here are navigating what it means to live inside someone else's idea of paradise.",
    category: 'Experiences',
    author: 'Amara Nkosi',
    readTime: 8,
    gradientFrom: '#1e3a5f',
    gradientTo: '#374151',
    theme: 'news-column',
    content: hoedspruitTownContent,
  },
  {
    id: 'lowveld-flavours',
    title: 'From Mopane Worms to Rooibos: The Lowveld Flavour Atlas',
    excerpt:
      'A researcher and a chef drive 2 400 km across the Lowveld tasting everything edible — documenting 118 indigenous ingredients that rarely appear on any restaurant menu.',
    category: 'Culinary Journeys',
    author: 'Dr. Zanele Mokoena',
    readTime: 10,
    gradientFrom: '#92400e',
    gradientTo: '#3f3f46',
    theme: 'timeline',
    content: lowveldFlavoursContent,
  },
  {
    id: 'george-road',
    title: 'The Road from George: Slow Travel on the Garden Route',
    excerpt:
      'Forget the N2. A travel writer takes the forgotten back roads from George to Knysna on foot and bicycle, staying with farming families and finding the coast South Africa forgot to Instagram.',
    category: 'Experiences',
    author: 'Pieter van der Berg',
    readTime: 7,
    gradientFrom: '#0369a1',
    gradientTo: '#134e4a',
    theme: 'minimal-one-pager',
    content: georgeRoadContent,
  },
  {
    id: 'elephant-memory',
    title: 'Forty Years of Watching Elephants Remember',
    excerpt:
      'Researcher Cynthia Masondo has tracked the same Kruger herd since 1986. What they have taught her about grief, decision-making, and matriarchal leadership has reshaped conservation science.',
    category: 'People & Culture',
    author: 'Cynthia Masondo',
    readTime: 12,
    gradientFrom: '#374151',
    gradientTo: '#1f2937',
    theme: 'page-flip',
    content: elephantMemoryContent,
  },
  {
    id: 'venda-pottery',
    title: "Venda Pottery's Unbroken Thread",
    excerpt:
      "In Limpopo's Venda region, clay pots are still made the same way they were a thousand years ago. Three women from the same family show what continuity looks like — and what it costs.",
    category: 'Heritage & Traditions',
    author: 'Faith Mukhuba',
    readTime: 6,
    gradientFrom: '#7f1d1d',
    gradientTo: '#451a03',
    theme: 'podcast-transcript',
    content: vendaPotteryContent,
  },
];

export const CATEGORIES: Category[] = [
  'People & Culture',
  'Heritage & Traditions',
  'Wildlife & Coexistence',
  'Culinary Journeys',
  'Artisan & Craft',
  'Experiences',
];

export const CATEGORY_COLOR: Record<Category, string> = {
  'People & Culture': 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  'Heritage & Traditions': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  'Wildlife & Coexistence': 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  'Culinary Journeys': 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  'Artisan & Craft': 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  'Experiences': 'bg-teal-500/15 text-teal-300 border-teal-500/30',
};

export const THEME_LABELS: Record<string, string> = {
  'page-flip': 'Page-Flip Magazine',
  'scrolling-long-form': 'Scrolling Long-Form',
  'magazine-grid': 'Magazine Grid',
  'photo-essay': 'Photo Essay',
  'news-column': 'News Column',
  'timeline': 'Timeline',
  'minimal-one-pager': 'Minimal One-Pager',
  'data-viz': 'Data Visualisation',
  'grid': 'Grid',
  'podcast-transcript': 'Podcast Transcript',
};
